import { apiClient } from '@copro/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from '../../../components/GlobalSnackbar/snackbarStore';
import { useSpace } from '../../../hooks/useSpace';
import { keys } from './keys';

type SaveQuoteCommand = {
  groupId: string;
  /** Updates this quote when set, creates one otherwise. */
  quoteId?: string;
  provider: string;
  fileUrl: string | null;
  issuedAt: string | null;
  expirationDate: string | null;
  /** Value of each criterion, keyed by column id. */
  values: Record<string, string>;
};

async function saveQuote(command: SaveQuoteCommand) {
  const row = {
    group_id: command.groupId,
    provider: command.provider,
    file_url: command.fileUrl,
    issued_at: command.issuedAt,
    expiration_date: command.expirationDate,
  };

  let quoteId = command.quoteId;

  if (quoteId) {
    const { error } = await apiClient.from('quotes').update(row).eq('id', quoteId);

    if (error) {
      throw error;
    }

    const { error: deleteError } = await apiClient.from('quote_values').delete().eq('quote_id', quoteId);

    if (deleteError) {
      throw deleteError;
    }
  } else {
    const { data, error } = await apiClient.from('quotes').insert(row).select('id').single();

    if (error) {
      throw error;
    }

    quoteId = data.id;
  }

  const values = Object.entries(command.values)
    .filter(([, value]) => value !== '')
    .map(([columnId, value]) => ({ quote_id: quoteId!, column_id: columnId, value }));

  if (values.length > 0) {
    const { error } = await apiClient.from('quote_values').insert(values);

    if (error) {
      throw error;
    }
  }
}

export function useSaveQuote() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: saveQuote,
    onSuccess: (_data, command) => {
      queryClient.invalidateQueries({ queryKey: keys.groupQuotes(command.groupId) });

      if (!command.quoteId) {
        queryClient.invalidateQueries({ queryKey: keys.groupsList(currentSpace!.id) });
      }

      enqueueSnackbar(command.quoteId ? 'Devis modifié.' : 'Devis ajouté.', 'success');
    },
  });
}
